import React, { useEffect, useRef, useState } from "react";
import { Upload, X, FileText, Loader2, Send, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CONSENT = {
  hu: { pre: "Megerősítem, hogy elmúltam 18 éves, és elolvastam és elfogadom az", privacy: "Adatvédelmi tájékoztatót", and: "és a", terms: "Felhasználási feltételeket" },
  en: { pre: "I confirm I am over 18 and have read and accept the", privacy: "Privacy Policy", and: "and the", terms: "Terms of Use" },
  de: { pre: "Ich bestätige, dass ich über 18 bin und die", privacy: "Datenschutzerklärung", and: "und die", terms: "Nutzungsbedingungen" },
  fr: { pre: "Je confirme avoir plus de 18 ans et avoir lu et accepté la", privacy: "Politique de confidentialité", and: "et les", terms: "Conditions d'utilisation" },
  cs: { pre: "Potvrzuji, že je mi více než 18 let, přečetl(a) jsem si a souhlasím se", privacy: "Zásadami ochrany osobních údajů", and: "a s", terms: "Podmínkami použití" },
};

function FieldLabel({ children, required }) {
  return (
    <Label className="mb-1.5 block text-[12px] font-medium text-zinc-300">
      {children} {required && <span className="text-red-400">*</span>}
    </Label>
  );
}

export function ReportForm({ onSuccess, apiPath = "/reports" }) {
  const { lang, tr } = useLang();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    platform: "",
    content_url: "",
    category: "",
    description: "",
    reporter_name: "",
    reporter_email: "",
    anonymous: false,
    good_faith: false,
    age_consent: false,
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((a) => [...a, r.data]);
      } catch (err) {
        setErrors((prev) => ({ ...prev, files: err?.response?.data?.detail || tr.errGeneric }));
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeFile = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const validate = () => {
    const e = {};
    if (!form.platform.trim()) e.platform = tr.required;
    if (!form.content_url.trim()) e.content_url = tr.required;
    if (!form.category) e.category = tr.required;
    if (!form.description.trim()) e.description = tr.required;
    if (!form.anonymous) {
      if (!form.reporter_email.trim()) e.reporter_email = tr.required;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.reporter_email)) e.reporter_email = tr.invalidEmail;
    }
    if (!form.good_faith) e.good_faith = tr.mustAgree;
    if (!form.age_consent) e.age_consent = tr.mustAgree;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      document.querySelector("[data-error='true']")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        platform: form.platform.trim(),
        content_url: form.content_url.trim(),
        category: form.category,
        description: form.description.trim(),
        reporter_name: form.anonymous ? "" : form.reporter_name.trim(),
        reporter_email: form.anonymous ? null : form.reporter_email.trim(),
        anonymous: form.anonymous,
        good_faith: form.good_faith,
        age_consent: form.age_consent,
        language: lang,
        attachments: attachments.map((a) => ({ id: a.id, filename: a.filename, url: a.url, size: a.size })),
      };
      const r = await api.post(apiPath, payload);
      onSuccess({ ...r.data, email: payload.reporter_email, anonymous: form.anonymous });
    } catch (err) {
      setErrors((prev) => ({ ...prev, submit: err?.response?.data?.detail || tr.errGeneric }));
    } finally {
      setSubmitting(false);
    }
  };

  const errCls = "mt-1 text-[11px] text-red-400";

  return (
    <form onSubmit={submit} className="space-y-7" data-testid="report-form" noValidate>
      {/* content */}
      <div>
        <div className="label-caps mb-3">{tr.secContent}</div>
        <div className="space-y-4">
          <div data-error={!!errors.platform}>
            <FieldLabel required>{tr.platform}</FieldLabel>
            <Input
              data-testid="input-platform"
              value={form.platform}
              onChange={(e) => set("platform", e.target.value)}
              placeholder={tr.platformPh}
              className="border-zinc-800 bg-zinc-950 text-[14px]"
            />
            {errors.platform && <div className={errCls}>{errors.platform}</div>}
          </div>
          <div data-error={!!errors.content_url}>
            <FieldLabel required>{tr.contentUrl}</FieldLabel>
            <Input
              data-testid="input-url"
              value={form.content_url}
              onChange={(e) => set("content_url", e.target.value)}
              placeholder={tr.contentUrlPh}
              className="border-zinc-800 bg-zinc-950 font-mono text-[13px]"
            />
            {errors.content_url && <div className={errCls}>{errors.content_url}</div>}
          </div>
          <div data-error={!!errors.category}>
            <FieldLabel required>{tr.category}</FieldLabel>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="select-category" className="border-zinc-800 bg-zinc-950 text-[14px]">
                <SelectValue placeholder={tr.categoryPh} />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                {categories.map((c) => (
                  <SelectItem key={c.key} value={c.key} className="text-[13px] text-zinc-300 focus:bg-zinc-800">
                    {c[lang]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && <div className={errCls}>{errors.category}</div>}
          </div>
          <div data-error={!!errors.description}>
            <FieldLabel required>{tr.description}</FieldLabel>
            <Textarea
              data-testid="input-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder={tr.descriptionPh}
              className="min-h-[110px] resize-y border-zinc-800 bg-zinc-950 text-[14px]"
            />
            {errors.description && <div className={errCls}>{errors.description}</div>}
          </div>
        </div>
      </div>

      {/* evidence */}
      <div>
        <div className="label-caps mb-1.5">{tr.secEvidence}</div>
        <p className="mb-3 text-[12px] text-zinc-500">{tr.evidenceHint}</p>
        <div data-testid="csam-warning" className="mb-3 flex items-start gap-2.5 rounded-lg border border-red-600/50 bg-red-950/40 px-3.5 py-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-red-400" />
          <p className="text-[12.5px] font-medium leading-relaxed text-red-200">{tr.csamWarning}</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
          onChange={handleFiles}
          className="hidden"
          data-testid="input-files"
        />
        <Button
          type="button"
          variant="outline"
          data-testid="add-files-btn"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="h-9 border-zinc-800 bg-zinc-900 text-[13px] text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100"
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {uploading ? tr.uploading : tr.addFiles}
        </Button>
        {errors.files && <div className={errCls}>{errors.files}</div>}
        {attachments.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="attachment-list">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2">
                <FileText size={15} className="text-zinc-500" />
                <span className="flex-1 truncate text-[13px] text-zinc-300">{a.filename}</span>
                <span className="text-[11px] text-zinc-600">{Math.round(a.size / 1024)} KB</span>
                <button type="button" onClick={() => removeFile(a.id)} className="text-zinc-500 hover:text-red-400" aria-label={tr.remove}>
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* reporter */}
      <div>
        <div className="label-caps mb-3">{tr.secReporter}</div>
        <label className="mb-4 flex cursor-pointer items-start gap-2.5">
          <Checkbox
            data-testid="check-anonymous"
            checked={form.anonymous}
            onCheckedChange={(v) => set("anonymous", !!v)}
            className="mt-0.5 border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
          />
          <span className="text-[13px] text-zinc-300">{tr.anonymous}</span>
        </label>
        {!form.anonymous && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel>{tr.name}</FieldLabel>
              <Input
                data-testid="input-name"
                value={form.reporter_name}
                onChange={(e) => set("reporter_name", e.target.value)}
                placeholder={tr.namePh}
                className="border-zinc-800 bg-zinc-950 text-[14px]"
              />
            </div>
            <div data-error={!!errors.reporter_email}>
              <FieldLabel required>{tr.email}</FieldLabel>
              <Input
                data-testid="input-email"
                type="email"
                value={form.reporter_email}
                onChange={(e) => set("reporter_email", e.target.value)}
                placeholder={tr.emailPh}
                className="border-zinc-800 bg-zinc-950 text-[14px]"
              />
              {errors.reporter_email ? (
                <div className={errCls}>{errors.reporter_email}</div>
              ) : (
                <div className="mt-1 text-[11px] text-zinc-600">{tr.emailHelp}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* declaration + consent + submit */}
      <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
        <label className="flex cursor-pointer items-start gap-2.5" data-error={!!errors.good_faith}>
          <Checkbox
            data-testid="check-goodfaith"
            checked={form.good_faith}
            onCheckedChange={(v) => set("good_faith", !!v)}
            className="mt-0.5 border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
          />
          <span className="text-[12px] leading-relaxed text-zinc-400">{tr.goodFaith}</span>
        </label>
        <label className="flex cursor-pointer items-start gap-2.5" data-error={!!errors.age_consent}>
          <Checkbox
            data-testid="check-age-consent"
            checked={form.age_consent}
            onCheckedChange={(v) => set("age_consent", !!v)}
            className="mt-0.5 border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
          />
          <span className="text-[12px] leading-relaxed text-zinc-400">
            {CONSENT[lang].pre}{" "}
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{CONSENT[lang].privacy}</a>
            {" "}{CONSENT[lang].and}{" "}
            <a href="/terms" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">{CONSENT[lang].terms}</a>.
          </span>
        </label>
        {(errors.good_faith || errors.age_consent) && (
          <div className="text-[11px] text-red-400">{errors.age_consent || errors.good_faith}</div>
        )}
      </div>

      {errors.submit && (
        <div className="rounded-md border border-red-600/40 bg-red-600/10 px-3 py-2 text-[13px] text-red-300">
          {errors.submit}
        </div>
      )}

      <Button
        type="submit"
        data-testid="submit-report-btn"
        disabled={submitting || uploading}
        className="h-11 w-full bg-blue-600 text-[14px] font-medium text-white hover:bg-blue-700 sm:w-auto sm:px-8"
      >
        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {submitting ? tr.submitting : tr.submit}
      </Button>
    </form>
  );
}
